import { useState, useMemo, useCallback, useEffect, memo, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Trash2, Plus, User, Package, CreditCard, Calendar, Loader2, History, Eye, Edit, Receipt, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { searchMatch, formatCPF, formatPhone, formatCNPJ, formatCPFCNPJ, validateCPFCNPJ, formatDateDisplay } from '@/utils/formatters';
import { ProductModal } from '@/components/ProductModal';
import AiAnalysisTrigger from '@/components/simulator/AiAnalysisTrigger';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Estados brasileiros
const ESTADOS_BRASIL = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

// Função para formatar valores monetários no padrão brasileiro
const formatMoney = (value) => {
  const num = typeof value === 'number' ? value : parseFloat(value) || 0;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const NewOrderModal = memo(function NewOrderModal({
  isOpen,
  onOpenChange,
  editingOrder,
  products,
  customers,
  bankAccounts,
  productCategories,
  proximoNumeroPedido,
  onSubmitSuccess,
  triggerButton
}) {
  // ============ Estados do formulário ============
  const [formData, setFormData] = useState({
    numero_pedido: '',
    cliente_id: '',
    cliente_avulso: { nome: '', telefone: '', endereco: '' },
    produtos: [],
    forma_pagamento: 'PIX',
    data_pedido: '',
    data_entrega_prevista: '',
    observacoes: '',
    pagamentos: [],
    desconto_tipo: '',
    desconto_valor: '',
    emitir_nfe: false,
    // Campos de parcelamento (contas a receber)
    gerar_parcelas: false,
    num_parcelas: 2,
    valor_entrada: '',
    primeira_parcela_data: '',
    modo_data_parcelas: 'automatico', // 'automatico' ou 'manual'
    parcelas_customizadas: [] // Array de {valor, data_vencimento}
  });
  
  const [pagamentoAtual, setPagamentoAtual] = useState({
    forma_pagamento: 'PIX',
    valor: '',
    conta_id: '',
    observacao: '',
    bandeira: '',
    parcelas: 1,
    nsu: '',
    data_pagamento: new Date().toISOString().split('T')[0] // Data de hoje no formato YYYY-MM-DD
  });
  
  const [pagamentosExistentesIds, setPagamentosExistentesIds] = useState(new Set());
  
  // ============ Estados do cliente ============
  const [clienteMode, setClienteMode] = useState('existente');
  const [clienteSearch, setClienteSearch] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [highlightedClienteIndex, setHighlightedClienteIndex] = useState(-1);
  const [salvandoCliente, setSalvandoCliente] = useState(false);
  const produtoSearchRef = useRef(null);
  const quantityInputRef = useRef(null);
  const produtoListRef = useRef(null);
  const clienteListRef = useRef(null);
  const [novoCliente, setNovoCliente] = useState({
    nome: '', email: '', telefone: '', cpf: '', endereco: '', cidade: '', estado: '', cep: '', tipo: 'PF', bairro: ''
  });
  const [cidadesNovoCliente, setCidadesNovoCliente] = useState([]);
  const [loadingCidadesNovoCliente, setLoadingCidadesNovoCliente] = useState(false);
  const [buscandoCNPJ, setBuscandoCNPJ] = useState(false);
  const [buscandoCEP, setBuscandoCEP] = useState(false);
  
  // ============ Estados do histórico do cliente ============
  const [showHistorico, setShowHistorico] = useState(false);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [historicoPedidos, setHistoricoPedidos] = useState([]);

  // ============ Estados dos modais Ver/Editar Cliente ============
  const [showViewCliente, setShowViewCliente] = useState(false);
  const [showEditCliente, setShowEditCliente] = useState(false);
  const [viewClienteLoading, setViewClienteLoading] = useState(false);
  const [viewClienteData, setViewClienteData] = useState(null); // { customer, orders, saldoDevedor, totalPedidos }
  const [editClienteForm, setEditClienteForm] = useState({
    nome: '', telefone: '', email: '', cpf_cnpj: '', endereco: '', bairro: '', cidade: '', estado: '', cep: '', tipo: 'PF'
  });
  const [savingEditCliente, setSavingEditCliente] = useState(false);

  // Reseta modais filhos quando o modal principal (Novo/Editar Pedido) fecha
  useEffect(() => {
    if (!open) {
      setShowViewCliente(false);
      setShowEditCliente(false);
      setShowHistorico(false);
    }
  }, [open]);
  
  // ============ Estados do produto ============
  const [produtoMode, setProdutoMode] = useState('catalogo');
  const [produtoSearch, setProdutoSearch] = useState('');
  const [showProdutoDropdown, setShowProdutoDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [editingProdutoIdx, setEditingProdutoIdx] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false); // Modal de cadastro completo
  const [localProducts, setLocalProducts] = useState(products || []); // Lista local de produtos
  const [produtoAtual, setProdutoAtual] = useState({
    produto_id: '', nome: '', variacao_id: '', quantidade: 1, preco_unitario: 0, custo_unitario: 0, observacoes: ''
  });
  const [novoProduto, setNovoProduto] = useState({
    nome: '', codigo: '', ncm: '', descricao: '', custo: '', preco: '', categoria: '', unidade: 'UN', estoque_minimo: '', observacoes: '',
    volume_m3: '', tipo_madeira_id: '', mao_de_obra: '', pintura: '', pecas_madeira: []
  });
  
  // ============ Estado para nova peça de madeira ============
  const [novaPecaMadeira, setNovaPecaMadeira] = useState({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
  
  // ============ Estados para tipos de madeira ============
  const [tiposMadeira, setTiposMadeira] = useState([]);
  
  // ============ Estados para variações do produto ============
  const [variacoesProduto, setVariacoesProduto] = useState([]);
  const [variacoesSelecionadas, setVariacoesSelecionadas] = useState([]);
  const [loadingVariacoes, setLoadingVariacoes] = useState(false);
  
  // Carregar tipos de madeira
  useEffect(() => {
    const fetchTiposMadeira = async () => {
      try {
        const res = await axios.get(`${API}/tipos-madeira`);
        setTiposMadeira(res.data || []);
      } catch (error) {
        console.error('Erro ao carregar tipos de madeira:', error);
      }
    };
    fetchTiposMadeira();
  }, []);
  
  // ============ Funções de cálculo de madeira ============
  const calcularVolumePeca = (peca) => {
    const comp = parseFloat(peca.comprimento) || 0;
    const larg = parseFloat(peca.largura) || 0;
    const esp = parseFloat(peca.espessura) || 0;
    const qtd = parseInt(peca.quantidade) || 1;
    // Converter de cm³ para m³ (dividir por 1.000.000)
    return ((comp * larg * esp) / 1000000) * qtd;
  };

  const calcularVolumeTotal = (pecas) => {
    return pecas.reduce((total, peca) => total + calcularVolumePeca(peca), 0);
  };

  const calcularCustoMadeira = (pecas, tipoMadeiraId) => {
    const tipo = tiposMadeira.find(t => t.id === tipoMadeiraId);
    if (!tipo) return 0;
    const volumeTotal = calcularVolumeTotal(pecas);
    return volumeTotal * tipo.preco_m3;
  };

  const handleAddPecaMadeira = () => {
    if (!novaPecaMadeira.comprimento || !novaPecaMadeira.largura || !novaPecaMadeira.espessura) {
      toast.error('Preencha comprimento, largura e espessura');
      return;
    }
    const novaPecaComId = {
      ...novaPecaMadeira,
      id: Date.now()
    };
    const novasPecas = [...(novoProduto.pecas_madeira || []), novaPecaComId];
    const volumeTotal = calcularVolumeTotal(novasPecas);
    const custoMadeira = calcularCustoMadeira(novasPecas, novoProduto.tipo_madeira_id);
    const maoDeObra = parseFloat(novoProduto.mao_de_obra) || 0;
    const pintura = parseFloat(novoProduto.pintura) || 0;
    const custoTotal = custoMadeira + maoDeObra + pintura;
    
    setNovoProduto({
      ...novoProduto,
      pecas_madeira: novasPecas,
      volume_m3: volumeTotal.toFixed(6),
      custo: custoTotal.toFixed(2)
    });
    setNovaPecaMadeira({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
    toast.success('Peça adicionada!');
  };

  const handleRemovePecaMadeira = (pecaId) => {
    const novasPecas = (novoProduto.pecas_madeira || []).filter(p => p.id !== pecaId);
    const volumeTotal = calcularVolumeTotal(novasPecas);
    const custoMadeira = calcularCustoMadeira(novasPecas, novoProduto.tipo_madeira_id);
    const maoDeObra = parseFloat(novoProduto.mao_de_obra) || 0;
    const pintura = parseFloat(novoProduto.pintura) || 0;
    const custoTotal = custoMadeira + maoDeObra + pintura;
    
    setNovoProduto({
      ...novoProduto,
      pecas_madeira: novasPecas,
      volume_m3: volumeTotal.toFixed(6),
      custo: custoTotal.toFixed(2)
    });
  };
  
  // ============ Estados de parcelamento ============
  const [parcelamentoAtivo, setParcelamentoAtivo] = useState(false);
  const [numParcelas, setNumParcelas] = useState(2);
  const [valorEntrada, setValorEntrada] = useState(0);
  const [primeiraParcela, setPrimeiraParcela] = useState('');
  const [parcelasGeradas, setParcelasGeradas] = useState([]);
  
  // ============ Estado de loading ============
  const [saving, setSaving] = useState(false);
  
  // ============ Filtros memoizados ============
  const filteredCustomers = useMemo(() => {
    if (!clienteSearch) return [];
    const searchDigits = clienteSearch.replace(/\D/g, '');
    return customers.filter(c => {
      const phoneDigits = c.telefone?.replace(/\D/g, '') || '';
      const cpfDigits = c.cpf_cnpj?.replace(/\D/g, '') || '';
      return (
        searchMatch(c.nome, clienteSearch) ||
        (searchDigits && phoneDigits.includes(searchDigits)) ||
        (searchDigits && cpfDigits.includes(searchDigits))
      );
    }).slice(0, 10);
  }, [customers, clienteSearch]);
  
  const filteredProducts = useMemo(() => {
    if (!produtoSearch) return [];
    return products.filter(p => searchMatch(p.nome, produtoSearch)).slice(0, 10);
  }, [products, produtoSearch]);
  
  // ============ Cálculos ============
  const totalCusto = formData.produtos.reduce((sum, p) => sum + (p.custo_unitario * p.quantidade), 0);
  const totalVenda = formData.produtos.reduce((sum, p) => sum + (p.preco_unitario * p.quantidade), 0);
  
  const calcularDesconto = useCallback(() => {
    if (!formData.desconto_tipo || !formData.desconto_valor) return 0;
    const desconto = parseFloat(formData.desconto_valor) || 0;
    if (formData.desconto_tipo === 'percentual') {
      return totalVenda * (desconto / 100);
    }
    return desconto;
  }, [formData.desconto_tipo, formData.desconto_valor, totalVenda]);
  
  const valorDesconto = calcularDesconto();
  const totalComDesconto = totalVenda - valorDesconto;
  const totalJaPago = formData.pagamentos?.reduce((sum, p) => sum + (parseFloat(p.valor) || 0), 0) || 0;
  const saldoRestante = totalComDesconto - totalJaPago;
  
  // ============ Gerar parcelas automaticamente ============
  useEffect(() => {
    if (!parcelamentoAtivo || saldoRestante <= 0) {
      if (formData.parcelas_customizadas.length > 0) {
        setFormData(prev => ({ ...prev, parcelas_customizadas: [] }));
      }
      return;
    }
    
    const entrada = parseFloat(formData.valor_entrada) || 0;
    const valorAParcelar = saldoRestante - entrada;
    if (valorAParcelar <= 0) return;
    
    const numParcelas = formData.num_parcelas || 2;
    const valorParcela = valorAParcelar / numParcelas;
    const dataBase = formData.primeira_parcela_data 
      ? new Date(formData.primeira_parcela_data + 'T12:00:00')
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    
    // Só regenerar se o número de parcelas mudou ou se está vazio
    if (formData.parcelas_customizadas.length !== numParcelas) {
      const novasParcelas = Array.from({ length: numParcelas }).map((_, idx) => {
        const dataVenc = new Date(dataBase);
        dataVenc.setDate(dataVenc.getDate() + (30 * idx));
        return {
          numero: idx + 1,
          valor: Math.round(valorParcela * 100) / 100,
          data_vencimento: dataVenc.toISOString().split('T')[0]
        };
      });
      setFormData(prev => ({ ...prev, parcelas_customizadas: novasParcelas }));
    }
  }, [parcelamentoAtivo, saldoRestante, formData.valor_entrada, formData.num_parcelas, formData.primeira_parcela_data]);
  
  // ============ Atualizar parcela individual ============
  const handleUpdateParcela = useCallback((index, field, value) => {
    setFormData(prev => {
      const novasParcelas = [...prev.parcelas_customizadas];
      novasParcelas[index] = { ...novasParcelas[index], [field]: value };
      return { ...prev, parcelas_customizadas: novasParcelas };
    });
  }, []);
  
  // ============ Recalcular parcelas uniformemente ============
  const handleRecalcularParcelas = useCallback(() => {
    const entrada = parseFloat(formData.valor_entrada) || 0;
    const valorAParcelar = saldoRestante - entrada;
    if (valorAParcelar <= 0) return;
    
    const numParcelas = formData.num_parcelas || 2;
    const valorParcela = Math.round((valorAParcelar / numParcelas) * 100) / 100;
    const dataBase = formData.primeira_parcela_data 
      ? new Date(formData.primeira_parcela_data + 'T12:00:00')
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    
    const novasParcelas = Array.from({ length: numParcelas }).map((_, idx) => {
      const dataVenc = new Date(dataBase);
      dataVenc.setDate(dataVenc.getDate() + (30 * idx));
      return {
        numero: idx + 1,
        valor: valorParcela,
        data_vencimento: dataVenc.toISOString().split('T')[0]
      };
    });
    setFormData(prev => ({ ...prev, parcelas_customizadas: novasParcelas }));
  }, [saldoRestante, formData.valor_entrada, formData.num_parcelas, formData.primeira_parcela_data]);
  
  // ============ Buscar cidades ============
  const fetchCidadesNovoCliente = useCallback(async (uf) => {
    if (!uf) { setCidadesNovoCliente([]); return; }
    setLoadingCidadesNovoCliente(true);
    try {
      const response = await axios.get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios?orderBy=nome`);
      setCidadesNovoCliente(response.data.map(c => c.nome));
    } catch (error) {
      console.error('Erro ao buscar cidades:', error);
      setCidadesNovoCliente([]);
    } finally {
      setLoadingCidadesNovoCliente(false);
    }
  }, []);
  
  useEffect(() => {
    if (novoCliente.estado) {
      fetchCidadesNovoCliente(novoCliente.estado);
    } else {
      setCidadesNovoCliente([]);
    }
  }, [novoCliente.estado, fetchCidadesNovoCliente]);
  
  // ============ Buscar CNPJ ============
  const buscarCNPJ = async () => {
    const cnpj = novoCliente.cpf.replace(/\D/g, '');
    if (cnpj.length !== 14) {
      toast.error('CNPJ inválido');
      return;
    }
    setBuscandoCNPJ(true);
    try {
      const response = await axios.get(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
      const data = response.data;
      setNovoCliente(prev => ({
        ...prev,
        nome: data.razao_social || data.nome_fantasia || prev.nome,
        telefone: data.ddd_telefone_1 ? formatPhone(`${data.ddd_telefone_1}`) : prev.telefone,
        email: data.email || prev.email,
        endereco: `${data.logradouro || ''}${data.numero ? ', ' + data.numero : ''}${data.complemento ? ' - ' + data.complemento : ''}`.trim(),
        bairro: data.bairro || prev.bairro,
        cidade: data.municipio || prev.cidade,
        estado: data.uf || prev.estado,
        cep: data.cep ? data.cep.replace(/(\d{5})(\d{3})/, '$1-$2') : prev.cep
      }));
      toast.success('Dados do CNPJ carregados!');
    } catch (error) {
      toast.error('Erro ao buscar CNPJ');
    } finally {
      setBuscandoCNPJ(false);
    }
  };
  
  // ============ Buscar CEP ============
  const buscarCEP = async () => {
    const cep = novoCliente.cep.replace(/\D/g, '');
    if (cep.length !== 8) return;
    setBuscandoCEP(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
      const data = response.data;
      if (!data.erro) {
        setNovoCliente(prev => ({
          ...prev,
          endereco: `${data.logradouro || ''}${data.complemento ? ' - ' + data.complemento : ''}`.trim(),
          bairro: data.bairro || prev.bairro,
          cidade: data.localidade || prev.cidade,
          estado: data.uf || prev.estado
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setBuscandoCEP(false);
    }
  };
  
  // ============ Buscar histórico do cliente ============
  const handleBuscarHistorico = async () => {
    if (!formData.cliente_id) return;
    
    setHistoricoLoading(true);
    setShowHistorico(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers/${formData.cliente_id}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoricoPedidos(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      toast.error('Erro ao carregar histórico do cliente');
      setHistoricoPedidos([]);
    } finally {
      setHistoricoLoading(false);
    }
  };

  // ============ Ver Cliente (dados + saldo devedor + pedidos) ============
  const handleVerCliente = async () => {
    if (!formData.cliente_id) return;
    setViewClienteLoading(true);
    setShowViewCliente(true);
    try {
      const token = localStorage.getItem('token');
      const [custRes, ordersRes] = await Promise.all([
        axios.get(`${API}/customers/${formData.cliente_id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers/${formData.cliente_id}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const orders = ordersRes.data || [];
      // Saldo devedor = soma (total_com_desconto - total_pago) dos pedidos não cancelados
      const saldoDevedor = orders
        .filter(o => (o.status || '').toLowerCase() !== 'cancelado')
        .reduce((acc, o) => {
          const total = Number(o.total_com_desconto ?? o.total_venda ?? 0);
          const pago = Number(o.total_pago ?? 0);
          return acc + Math.max(0, total - pago);
        }, 0);
      setViewClienteData({ customer: custRes.data, orders, saldoDevedor, totalPedidos: orders.length });
    } catch (error) {
      console.error('Erro ao carregar dados do cliente:', error);
      toast.error('Erro ao carregar dados do cliente');
      setViewClienteData(null);
    } finally {
      setViewClienteLoading(false);
    }
  };

  // ============ Abrir modal de edição do cliente ============
  const handleAbrirEditCliente = async () => {
    if (!formData.cliente_id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/customers/${formData.cliente_id}`, { headers: { Authorization: `Bearer ${token}` } });
      const c = res.data || {};
      setEditClienteForm({
        nome: c.nome || '',
        telefone: c.telefone || '',
        email: c.email || '',
        cpf_cnpj: c.cpf_cnpj || '',
        endereco: c.endereco || '',
        bairro: c.bairro || '',
        cidade: c.cidade || '',
        estado: c.estado || '',
        cep: c.cep || '',
        tipo: (c.tipo === 'PJ' || c.tipo === 'pessoa_juridica') ? 'PJ' : 'PF',
      });
      setShowEditCliente(true);
    } catch (error) {
      toast.error('Erro ao carregar cliente para edição');
    }
  };

  // ============ Salvar edição do cliente ============
  const handleSalvarEditCliente = async () => {
    if (!editClienteForm.nome?.trim()) {
      toast.error('Informe o nome do cliente');
      return;
    }
    setSavingEditCliente(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/customers/${formData.cliente_id}`, editClienteForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cliente atualizado!');
      setClienteSearch(editClienteForm.nome);
      setShowEditCliente(false);
      // Se o modal de View está aberto, recarregar
      if (showViewCliente) handleVerCliente();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar cliente');
    } finally {
      setSavingEditCliente(false);
    }
  };
  
  // ============ Salvar novo cliente e vincular ao pedido ============
  const handleSalvarNovoCliente = async () => {
    if (!novoCliente.nome) {
      toast.error('Informe o nome do cliente');
      return;
    }
    
    if (novoCliente.cpf && !validateCPFCNPJ(novoCliente.cpf)) {
      const tipo = novoCliente.tipo === 'PF' ? 'CPF' : 'CNPJ';
      toast.error(`${tipo} inválido! Verifique os números digitados.`);
      return;
    }
    
    setSalvandoCliente(true);
    try {
      const token = localStorage.getItem('token');
      const clienteData = {
        nome: novoCliente.nome,
        email: novoCliente.email || '',
        telefone: novoCliente.telefone || '',
        cpf_cnpj: novoCliente.cpf || '',
        endereco: novoCliente.endereco || '',
        cidade: novoCliente.cidade || '',
        estado: novoCliente.estado || '',
        cep: novoCliente.cep || '',
        bairro: novoCliente.bairro || '',
        tipo: novoCliente.tipo || 'PF'
      };
      
      const response = await axios.post(`${API}/customers`, clienteData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const clienteCriado = response.data;
      
      // Vincular o cliente ao pedido
      setFormData(prev => ({ ...prev, cliente_id: clienteCriado.id }));
      setClienteSearch(clienteCriado.nome);
      setClienteMode('existente');
      
      // Limpar formulário de novo cliente
      setNovoCliente({ nome: '', email: '', telefone: '', cpf: '', endereco: '', cidade: '', estado: '', cep: '', tipo: 'PF', bairro: '' });
      
      toast.success(`Cliente "${clienteCriado.nome}" salvo e vinculado ao pedido!`);
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar cliente');
    } finally {
      setSalvandoCliente(false);
    }
  };
  
  // ============ Reset do formulário ============
  const resetForm = useCallback(() => {
    setFormData({
      numero_pedido: '',
      cliente_id: '',
      cliente_avulso: { nome: '', telefone: '', endereco: '' },
      produtos: [],
      forma_pagamento: 'PIX',
      data_pedido: '',
      data_entrega_prevista: '',
      observacoes: '',
      pagamentos: [],
      desconto_tipo: '',
      desconto_valor: '',
      emitir_nfe: false,
      gerar_parcelas: false,
      num_parcelas: 2,
      valor_entrada: '',
      primeira_parcela_data: '',
      modo_data_parcelas: 'automatico',
      parcelas_customizadas: []
    });
    setClienteMode('existente');
    setClienteSearch('');
    setNovoCliente({ nome: '', email: '', telefone: '', cpf: '', endereco: '', cidade: '', estado: '', cep: '', tipo: 'PF', bairro: '' });
    setProdutoMode('catalogo');
    setProdutoSearch('');
    setProdutoAtual({ produto_id: '', nome: '', variacao_id: '', quantidade: 1, preco_unitario: 0, custo_unitario: 0, observacoes: '' });
    setNovoProduto({ nome: '', codigo: '', ncm: '', descricao: '', custo: '', preco: '', categoria: '', unidade: 'UN', estoque_minimo: '', observacoes: '' });
    setPagamentoAtual({ forma_pagamento: 'PIX', valor: '', conta_id: '', observacao: '', bandeira: '', parcelas: 1, nsu: '', data_pagamento: new Date().toISOString().split('T')[0] });
    setParcelamentoAtivo(false);
    setParcelasGeradas([]);
    setPagamentosExistentesIds(new Set());
  }, []);
  
  // ============ Carregar dados ao editar ============
  useEffect(() => {
    if (editingOrder && isOpen) {
      const produtos = editingOrder.itens || editingOrder.produtos || [];
      setFormData({
        numero_pedido: editingOrder.numero_pedido || '',
        cliente_id: editingOrder.cliente_id || '',
        cliente_avulso: editingOrder.cliente_avulso || { nome: '', telefone: '', endereco: '' },
        produtos: produtos.map(p => ({
          ...p,
          produto_id: p.produto_id || p.id,
          nome: p.nome || p.descricao || 'Produto',
          quantidade: p.quantidade || 1,
          preco_unitario: p.preco_unitario || p.preco || 0,
          custo_unitario: p.custo_unitario || p.custo || 0
        })),
        forma_pagamento: editingOrder.forma_pagamento || 'PIX',
        data_pedido: (editingOrder.data_pedido || '').substring(0, 10),
        data_entrega_prevista: (editingOrder.data_entrega_prevista || '').substring(0, 10),
        observacoes: editingOrder.observacoes || '',
        pagamentos: editingOrder.pagamentos || [],
        desconto_tipo: editingOrder.desconto_tipo || '',
        desconto_valor: editingOrder.desconto_valor ? String(editingOrder.desconto_valor) : '',
        emitir_nfe: editingOrder.emitir_nfe || false,
        gerar_parcelas: false,
        num_parcelas: 2,
        valor_entrada: '',
        primeira_parcela_data: '',
        modo_data_parcelas: 'automatico',
        parcelas_customizadas: []
      });
      setParcelamentoAtivo(false);
      setParcelasGeradas([]);
      
      if (editingOrder.pagamentos?.length) {
        setPagamentosExistentesIds(new Set(editingOrder.pagamentos.map(p => p.id)));
      }
      
      if (editingOrder.cliente_id) {
        const cliente = customers.find(c => c.id === editingOrder.cliente_id);
        if (cliente) {
          setClienteSearch(cliente.nome);
          setClienteMode('existente');
        }
      } else if (editingOrder.cliente_avulso?.nome) {
        setClienteMode('avulso');
      }
    } else if (!isOpen) {
      resetForm();
    }
  }, [editingOrder, isOpen, customers, resetForm]);
  
  // ============ Handler: Adicionar produto ============
  const handleAddProduto = useCallback(async () => {
    if (produtoMode === 'avulso' || produtoMode === 'novo') {
      if (!novoProduto.nome || !novoProduto.preco) {
        toast.error('Preencha nome e preço do produto');
        return;
      }
      
      let produtoId = `avulso_${Date.now()}`;
      
      // Sempre salvar no catálogo
      try {
        const token = localStorage.getItem('token');
        const produtoData = {
          nome: novoProduto.nome,
          descricao: novoProduto.descricao || '',
          categoria: novoProduto.categoria || '',
          unidade: novoProduto.unidade || 'UN',
          preco_base: parseFloat(novoProduto.preco) || 0,
          custo: parseFloat(novoProduto.custo) || 0,
          estoque: parseInt(novoProduto.estoque_minimo) || 0,
          ncm: novoProduto.ncm || '94036000',
          observacoes: novoProduto.observacoes || '',
          volume_m3: novoProduto.volume_m3 ? parseFloat(novoProduto.volume_m3) : null,
          tipo_madeira_id: novoProduto.tipo_madeira_id || null,
          pecas_madeira: novoProduto.pecas_madeira || [],
          mao_de_obra: novoProduto.mao_de_obra ? parseFloat(novoProduto.mao_de_obra) : null,
          pintura: novoProduto.pintura ? parseFloat(novoProduto.pintura) : null
        };
        
        const response = await axios.post(`${API}/products`, produtoData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        produtoId = response.data.id;
        toast.success(`Produto "${novoProduto.nome}" salvo no catálogo!`);
      } catch (error) {
        console.error('Erro ao salvar produto no catálogo:', error);
        toast.error('Erro ao salvar no catálogo: ' + (error.response?.data?.detail || 'Erro desconhecido'));
        return; // Não adiciona ao pedido se falhar
      }
      
      setFormData(prev => ({
        ...prev,
        produtos: [...prev.produtos, {
          produto_id: produtoId,
          nome: novoProduto.nome,
          quantidade: 1,
          preco_unitario: parseFloat(novoProduto.preco) || 0,
          custo_unitario: parseFloat(novoProduto.custo) || 0,
          observacoes: novoProduto.observacoes,
          volume_m3: parseFloat(novoProduto.volume_m3) || null,
          tipo_madeira_id: novoProduto.tipo_madeira_id || null,
          tipo_madeira_nome: novoProduto.tipo_madeira_id 
            ? tiposMadeira.find(t => t.id === novoProduto.tipo_madeira_id)?.nome 
            : null
        }]
      }));
      setNovoProduto({ nome: '', codigo: '', ncm: '', descricao: '', custo: '', preco: '', categoria: '', unidade: 'UN', estoque_minimo: '', observacoes: '', volume_m3: '', tipo_madeira_id: '', mao_de_obra: '', pintura: '', pecas_madeira: [] });
      setNovaPecaMadeira({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
      setProdutoMode('catalogo');
      // Voltar foco para o campo de busca
      setTimeout(() => {
        produtoSearchRef.current?.focus();
      }, 100);
    } else if (produtoAtual.produto_id) {
      setFormData(prev => ({
        ...prev,
        produtos: [...prev.produtos, { ...produtoAtual }]
      }));
      setProdutoAtual({ produto_id: '', nome: '', variacao_id: '', quantidade: 1, preco_unitario: 0, custo_unitario: 0, observacoes: '', variacoes_selecionadas: [] });
      setProdutoSearch('');
      setVariacoesProduto([]);
      setVariacoesSelecionadas([]);
      // Voltar foco para o campo de busca
      setTimeout(() => {
        produtoSearchRef.current?.focus();
      }, 100);
    }
  }, [produtoMode, novoProduto, produtoAtual]);
  
  // ============ Handler: Remover produto ============
  const handleRemoveProduto = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      produtos: prev.produtos.filter((_, i) => i !== index)
    }));
  }, []);
  
  // ============ Handler: Selecionar produto ============
  const handleProdutoChange = useCallback(async (produtoId) => {
    if (produtoId === 'avulso') {
      setProdutoMode('avulso');
      setShowProdutoDropdown(false);
      setVariacoesProduto([]);
      setVariacoesSelecionadas([]);
      return;
    }
    const produto = products.find(p => p.id === produtoId);
    if (produto) {
      // Buscar informações do tipo de madeira se existir
      const tipoMadeira = produto.tipo_madeira_id 
        ? tiposMadeira.find(t => t.id === produto.tipo_madeira_id)
        : null;
      
      setProdutoAtual({
        produto_id: produto.id,
        nome: produto.nome,
        variacao_id: '',
        quantidade: 1,
        preco_unitario: produto.preco_base || 0,
        custo_unitario: produto.custo || 0,
        observacoes: '',
        codigo_omie: produto.codigo_omie,
        volume_m3: produto.volume_m3,
        tipo_madeira_id: produto.tipo_madeira_id,
        tipo_madeira_nome: tipoMadeira?.nome,
        tipo_madeira_preco: tipoMadeira?.preco_m3,
        variacoes_selecionadas: []
      });
      setProdutoSearch(produto.nome);
      
      // Carregar variações do produto
      setLoadingVariacoes(true);
      setVariacoesSelecionadas([]);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/products/${produtoId}/variacoes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const variacoesAtivas = (res.data || []).filter(v => v.ativo);
        setVariacoesProduto(variacoesAtivas);
      } catch (error) {
        console.error('Erro ao carregar variações:', error);
        setVariacoesProduto([]);
      } finally {
        setLoadingVariacoes(false);
      }
      
      // Focar no campo de quantidade após selecionar produto
      setTimeout(() => {
        if (quantityInputRef.current) {
          quantityInputRef.current.focus();
          quantityInputRef.current.select();
        }
      }, 100);
    }
    setShowProdutoDropdown(false);
  }, [products, tiposMadeira]);
  
  // ============ Handler: Alternar seleção de variação ============
  const handleToggleVariacao = useCallback((variacao) => {
    setVariacoesSelecionadas(prev => {
      const isSelected = prev.some(v => v.id === variacao.id);
      let novasVariacoes;
      
      if (isSelected) {
        // Remover variação
        novasVariacoes = prev.filter(v => v.id !== variacao.id);
      } else {
        // Adicionar variação
        novasVariacoes = [...prev, variacao];
      }
      
      // Recalcular preço e custo com as variações
      const produto = products.find(p => p.id === produtoAtual.produto_id);
      if (produto) {
        const precoBase = produto.preco_base || 0;
        const custoBase = produto.custo || 0;
        const volumeBase = produto.volume_m3 || 0;
        
        // Somar adicionais das variações
        let precoAdicional = 0;
        let custoAdicional = 0;
        let volumeAdicional = 0;
        
        novasVariacoes.forEach(v => {
          precoAdicional += (v.valor_venda_adicional || 0);
          custoAdicional += (v.custo_total_adicional || 0);
          volumeAdicional += (v.volume_m3_adicional || 0);
        });
        
        setProdutoAtual(prev => ({
          ...prev,
          preco_unitario: precoBase + precoAdicional,
          custo_unitario: custoBase + custoAdicional,
          volume_m3: volumeBase + volumeAdicional,
          variacoes_selecionadas: novasVariacoes.map(v => ({
            id: v.id,
            nome: v.nome,
            tipo: v.tipo,
            valor_adicional: v.valor_venda_adicional || 0,
            custo_adicional: v.custo_total_adicional || 0,
            volume_adicional: v.volume_m3_adicional || 0,
            tempo_adicional: v.tempo_adicional_horas || 0
          }))
        }));
      }
      
      return novasVariacoes;
    });
  }, [products, produtoAtual.produto_id]);

  // ============ Handler: Adicionar pagamento ============
  const handleAddPagamento = useCallback(() => {
    if (!pagamentoAtual.valor || parseFloat(pagamentoAtual.valor) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }
    // Validar bandeira para cartões
    if ((pagamentoAtual.forma_pagamento === 'Cartão Crédito' || pagamentoAtual.forma_pagamento === 'Cartão Débito') && !pagamentoAtual.bandeira) {
      toast.error('Selecione a bandeira do cartão');
      return;
    }
    const novoPagamento = {
      id: `pag_${Date.now()}`,
      ...pagamentoAtual,
      valor: parseFloat(pagamentoAtual.valor),
      data_pagamento: pagamentoAtual.data_pagamento || new Date().toISOString().split('T')[0],
      status: 'pendente'
    };
    setFormData(prev => ({
      ...prev,
      pagamentos: [...prev.pagamentos, novoPagamento]
    }));
    setPagamentoAtual({ forma_pagamento: 'PIX', valor: '', conta_id: '', observacao: '', bandeira: '', parcelas: 1, nsu: '', data_pagamento: new Date().toISOString().split('T')[0] });
  }, [pagamentoAtual]);

  // ============ Handler: Editar produto da lista ============
  const handleEditProduto = useCallback((idx) => {
    const item = formData.produtos[idx];
    setProdutoAtual({
      produto_id: item.produto_id,
      nome: item.nome,
      variacao_id: item.variacao_id || '',
      quantidade: item.quantidade,
      preco_unitario: item.preco_unitario,
      custo_unitario: item.custo_unitario || 0,
      observacoes: item.observacoes || '',
      volume_m3: item.volume_m3 || null,
      tipo_madeira_id: item.tipo_madeira_id || null,
      tipo_madeira_nome: item.tipo_madeira_nome || null
    });
    setEditingProdutoIdx(idx);
    setProdutoMode('catalogo');
  }, [formData.produtos]);

  // ============ Handler: Salvar edição do produto ============
  const handleSaveEditProduto = useCallback(() => {
    if (editingProdutoIdx === null) return;
    
    const novosProdutos = [...formData.produtos];
    novosProdutos[editingProdutoIdx] = {
      ...novosProdutos[editingProdutoIdx],
      quantidade: produtoAtual.quantidade,
      preco_unitario: produtoAtual.preco_unitario,
      observacoes: produtoAtual.observacoes
    };
    
    setFormData(prev => ({ ...prev, produtos: novosProdutos }));
    setProdutoAtual({ produto_id: '', nome: '', variacao_id: '', quantidade: 1, preco_unitario: 0, custo_unitario: 0, observacoes: '' });
    setEditingProdutoIdx(null);
    toast.success('Produto atualizado');
    
    // Voltar foco para busca
    setTimeout(() => {
      produtoSearchRef.current?.focus();
    }, 100);
  }, [editingProdutoIdx, formData.produtos, produtoAtual]);

  // ============ Handler: Cancelar edição do produto ============
  const handleCancelEditProduto = useCallback(() => {
    setProdutoAtual({ produto_id: '', nome: '', variacao_id: '', quantidade: 1, preco_unitario: 0, custo_unitario: 0, observacoes: '' });
    setEditingProdutoIdx(null);
  }, []);
  
  // ============ Handler: Remover pagamento ============
  const handleRemovePagamento = useCallback(async (index) => {
    const pagamento = formData.pagamentos[index];
    
    // Se é um pagamento existente (já salvo no banco), chamar API para excluir
    if (pagamento?.id && pagamentosExistentesIds.has(pagamento.id) && editingOrder) {
      if (!window.confirm('Deseja excluir este pagamento? A transação financeira e o saldo da conta bancária serão revertidos.')) {
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/orders/${editingOrder.id}/payments/${pagamento.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pagamento excluído e transação revertida!');
        
        // Atualizar lista de pagamentos existentes
        setPagamentosExistentesIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(pagamento.id);
          return newSet;
        });
      } catch (error) {
        toast.error('Erro ao excluir pagamento');
        return;
      }
    }
    
    // Remover do estado local
    setFormData(prev => ({
      ...prev,
      pagamentos: prev.pagamentos.filter((_, i) => i !== index)
    }));
  }, [formData.pagamentos, pagamentosExistentesIds, editingOrder]);
  
  // ============ Handler: Cadastrar novo cliente ============
  const handleCadastrarCliente = useCallback(async () => {
    if (!novoCliente.nome) {
      toast.error('Nome do cliente é obrigatório');
      return null;
    }
    
    // Validação de CPF/CNPJ
    if (novoCliente.cpf) {
      if (!validateCPFCNPJ(novoCliente.cpf)) {
        const tipo = novoCliente.tipo === 'PF' ? 'CPF' : 'CNPJ';
        toast.error(`${tipo} inválido! Verifique os números digitados.`);
        return null;
      }
    }
    
    try {
      const res = await axios.post(`${API}/customers`, {
        nome: novoCliente.nome,
        email: novoCliente.email || undefined,
        telefone: novoCliente.telefone || undefined,
        cpf_cnpj: novoCliente.cpf || undefined,
        endereco: novoCliente.endereco || undefined,
        bairro: novoCliente.bairro || undefined,
        cidade: novoCliente.cidade || undefined,
        estado: novoCliente.estado || undefined,
        cep: novoCliente.cep || undefined,
        tipo: novoCliente.tipo || 'PF'
      });
      toast.success('Cliente cadastrado!');
      return res.data.id;
    } catch (error) {
      toast.error('Erro ao cadastrar cliente');
      return null;
    }
  }, [novoCliente]);
  
  // ============ Handler: Submit ============
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (formData.produtos.length === 0) {
      toast.error('Adicione pelo menos um produto');
      return;
    }
    
    setSaving(true);
    try {
      let clienteId = formData.cliente_id;
      
      // Se modo é novo cliente, cadastrar primeiro
      if (clienteMode === 'novo') {
        clienteId = await handleCadastrarCliente();
        if (!clienteId) {
          setSaving(false);
          return;
        }
      }
      
      // Determinar número do pedido
      let numeroPedido = formData.numero_pedido;
      if (!numeroPedido) {
        numeroPedido = `PED-${String(proximoNumeroPedido).padStart(4, '0')}`;
      } else if (!numeroPedido.startsWith('PED-')) {
        numeroPedido = `PED-${numeroPedido}`;
      }
      
      const orderData = {
        numero_pedido: numeroPedido,
        cliente_id: clienteId || undefined,
        cliente_avulso: !clienteId ? formData.cliente_avulso : undefined,
        produtos: formData.produtos,
        forma_pagamento: formData.forma_pagamento || 'PIX',
        data_pedido: formData.data_pedido,
        data_entrega_prevista: formData.data_entrega_prevista,
        observacoes: formData.observacoes,
        pagamentos: editingOrder 
          ? formData.pagamentos.filter(p => !pagamentosExistentesIds.has(p.id))
          : (formData.pagamentos.length > 0 ? formData.pagamentos : undefined),
        desconto_tipo: formData.desconto_tipo || undefined,
        desconto_valor: formData.desconto_valor ? parseFloat(formData.desconto_valor) : undefined,
        total_custo: totalCusto,
        total_venda: totalVenda,
        total_com_desconto: totalComDesconto,
        emitir_nfe: formData.emitir_nfe || false,
        // Parcelamento para Contas a Receber (funciona tanto para novo pedido quanto edição)
        gerar_parcelas: parcelamentoAtivo,
        num_parcelas: parcelamentoAtivo ? formData.num_parcelas : undefined,
        valor_entrada: parcelamentoAtivo && formData.valor_entrada ? parseFloat(formData.valor_entrada) : undefined,
        primeira_parcela_data: parcelamentoAtivo ? formData.primeira_parcela_data : undefined,
        parcelas_customizadas: parcelamentoAtivo && formData.parcelas_customizadas.length > 0 
          ? formData.parcelas_customizadas 
          : undefined
      };
      
      if (editingOrder) {
        await axios.put(`${API}/orders/${editingOrder.id}`, orderData);
        toast.success('Pedido atualizado!');
      } else {
        await axios.post(`${API}/orders`, orderData);
        toast.success('Pedido criado!');
      }
      
      resetForm();
      onOpenChange(false);
      if (onSubmitSuccess) onSubmitSuccess();
      
    } catch (error) {
      console.error('Erro ao salvar pedido:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar pedido');
    } finally {
      setSaving(false);
    }
  }, [formData, clienteMode, editingOrder, proximoNumeroPedido, totalCusto, totalVenda, totalComDesconto, pagamentosExistentesIds, handleCadastrarCliente, resetForm, onOpenChange, onSubmitSuccess, parcelamentoAtivo]);
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      onOpenChange(open);
      if (!open) resetForm();
    }}>
      {triggerButton && <DialogTrigger asChild>{triggerButton}</DialogTrigger>}
      <DialogContent className="w-full max-w-[95vw] sm:max-w-3xl max-h-[80vh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
        <DialogHeader>
          <DialogTitle className="font-manrope">
            {editingOrder ? 'Editar Pedido' : 'Novo Pedido'}
          </DialogTitle>
          <DialogDescription>
            {editingOrder ? 'Atualize os dados do pedido' : 'Crie um novo pedido'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 1. Número do Pedido */}
          <div className="space-y-2">
            <Label>Número do Pedido</Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 bg-muted border border-r-0 rounded-l text-sm font-medium">PED-</span>
              <Input
                className="rounded-l-none"
                placeholder={String(proximoNumeroPedido).padStart(4, '0')}
                value={formData.numero_pedido.replace('PED-', '')}
                onChange={(e) => {
                  const num = e.target.value.replace(/\D/g, '');
                  setFormData({ ...formData, numero_pedido: num ? `PED-${num}` : '' });
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground">Próximo: PED-{String(proximoNumeroPedido).padStart(4, '0')}</p>
          </div>
          
          {/* 2. Datas */}
          <div className="flex gap-2">
            <div className="flex-1">
              <Label className="text-xs mb-1 block">Pedido</Label>
              <Input
                type="date"
                value={formData.data_pedido}
                onChange={(e) => setFormData({ ...formData, data_pedido: e.target.value })}
              />
            </div>
            <div className="flex-1">
              <Label className="text-xs mb-1 block">Entrega</Label>
              <Input
                type="date"
                value={formData.data_entrega_prevista}
                onChange={(e) => setFormData({ ...formData, data_entrega_prevista: e.target.value })}
              />
            </div>
          </div>
          
          {/* 3. Cliente */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="h-4 w-4 text-primary" />
                <span className="font-semibold text-sm">Cliente</span>
                <div className="flex gap-1 ml-auto">
                  <Button type="button" size="sm" variant={clienteMode === 'existente' ? 'default' : 'outline'} onClick={() => setClienteMode('existente')}>Existente</Button>
                  <Button type="button" size="sm" variant={clienteMode === 'novo' ? 'default' : 'outline'} onClick={() => setClienteMode('novo')}>+ Novo</Button>
                  <Button type="button" size="sm" variant={clienteMode === 'avulso' ? 'default' : 'outline'} onClick={() => setClienteMode('avulso')}>Avulso</Button>
                </div>
              </div>
              
              {clienteMode === 'existente' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      placeholder="Digite para pesquisar cliente..."
                      value={clienteSearch}
                      onChange={(e) => { setClienteSearch(e.target.value); setShowClienteDropdown(true); setHighlightedClienteIndex(0); }}
                      onFocus={() => { setShowClienteDropdown(true); setHighlightedClienteIndex(0); }}
                      onKeyDown={(e) => {
                        if (!showClienteDropdown || filteredCustomers.length === 0) return;
                        
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          const newIndex = Math.min(highlightedClienteIndex + 1, filteredCustomers.length - 1);
                          setHighlightedClienteIndex(newIndex);
                          // Scroll para o item
                          const container = clienteListRef.current;
                          if (container) {
                            const item = container.children[newIndex];
                            if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                          }
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          const newIndex = Math.max(highlightedClienteIndex - 1, 0);
                          setHighlightedClienteIndex(newIndex);
                          // Scroll para o item
                          const container = clienteListRef.current;
                          if (container) {
                            const item = container.children[newIndex];
                            if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                          }
                        } else if (e.key === 'Enter' && highlightedClienteIndex >= 0) {
                          e.preventDefault();
                          const selected = filteredCustomers[highlightedClienteIndex];
                          if (selected) {
                            setFormData({ ...formData, cliente_id: selected.id });
                            setClienteSearch(selected.nome);
                            setShowClienteDropdown(false);
                          }
                        } else if (e.key === 'Escape') {
                          setShowClienteDropdown(false);
                        }
                      }}
                    />
                    {showClienteDropdown && clienteSearch && (
                      <div ref={clienteListRef} className="absolute z-[100] w-full mt-1 bg-background border rounded-lg shadow-lg max-h-[250px] overflow-y-auto">
                        {filteredCustomers.map((c, idx) => (
                          <div
                            key={c.id}
                            className={`px-4 py-3 cursor-pointer ${highlightedClienteIndex === idx ? 'bg-primary/20' : 'hover:bg-muted/50'}`}
                            onClick={() => {
                              setFormData({ ...formData, cliente_id: c.id });
                              setClienteSearch(c.nome);
                              setShowClienteDropdown(false);
                            }}
                          >
                            <div className="font-semibold">{c.nome}</div>
                            <div className="text-sm text-muted-foreground">{c.telefone}</div>
                          </div>
                        ))}
                        {filteredCustomers.length === 0 && <div className="px-4 py-3 text-muted-foreground">Nenhum cliente encontrado</div>}
                      </div>
                    )}
                  </div>
                  
                  {/* Botões de ações do cliente selecionado */}
                  {formData.cliente_id && (
                    <div className="grid grid-cols-3 gap-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={handleVerCliente}
                        data-testid="btn-ver-cliente"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Ver Cliente
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={handleAbrirEditCliente}
                        data-testid="btn-editar-cliente"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={handleBuscarHistorico}
                        data-testid="btn-historico-cliente"
                      >
                        <History className="h-4 w-4 mr-1" />
                        Histórico
                      </Button>
                    </div>
                  )}
                  
                  {/* Mini-Modal de Histórico */}
                  {showHistorico && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50" onClick={() => setShowHistorico(false)}>
                      <div 
                        className="bg-background border rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden m-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
                          <h3 className="font-semibold flex items-center gap-2">
                            <History className="h-5 w-5 text-primary" />
                            Histórico de Compras - {clienteSearch}
                          </h3>
                          <Button variant="ghost" size="sm" onClick={() => setShowHistorico(false)}>✕</Button>
                        </div>
                        
                        <div className="overflow-y-auto max-h-[60vh] p-4">
                          {historicoLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                          ) : historicoPedidos.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                              <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                              <p>Nenhum pedido encontrado para este cliente.</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {historicoPedidos.map((pedido) => (
                                <div key={pedido.id} className="border rounded-lg p-3 hover:bg-muted/30 transition-colors">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-primary">{pedido.numero_pedido}</span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        pedido.status === 'Finalizado' ? 'bg-green-500/20 text-green-600' :
                                        pedido.status === 'Entregue' ? 'bg-blue-500/20 text-blue-600' :
                                        pedido.status === 'Aguardando' ? 'bg-yellow-500/20 text-yellow-600' :
                                        'bg-muted text-muted-foreground'
                                      }`}>
                                        {pedido.status}
                                      </span>
                                    </div>
                                    <span className="text-sm text-muted-foreground">
                                      {pedido.createdAt ? new Date(pedido.createdAt).toLocaleDateString('pt-BR') : '-'}
                                    </span>
                                  </div>
                                  
                                  {/* Lista de produtos */}
                                  <div className="space-y-1 mb-2">
                                    {(pedido.produtos || pedido.itens || []).map((item, idx) => {
                                      const precoUnit = item.preco_unitario || item.preco || 0;
                                      const qtd = item.quantidade || 1;
                                      return (
                                        <div key={idx} className="flex justify-between text-sm">
                                          <span className="text-muted-foreground">
                                            {qtd}x {item.nome || item.descricao || 'Produto'} 
                                            <span className="text-xs ml-1">(R$ {formatMoney(precoUnit)} un.)</span>
                                          </span>
                                          <span className="font-medium">R$ {formatMoney(precoUnit * qtd)}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  
                                  {/* Desconto e Total */}
                                  <div className="flex justify-between items-center pt-2 border-t">
                                    {pedido.desconto_valor > 0 && (
                                      <span className="text-xs text-red-500">
                                        Desconto: -R$ {formatMoney(pedido.desconto_valor)}
                                      </span>
                                    )}
                                    <span className="font-bold text-lg ml-auto">
                                      R$ {formatMoney(pedido.total_com_desconto || pedido.total_venda || 0)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className="px-4 py-3 border-t bg-muted/30 text-center text-sm text-muted-foreground">
                          {historicoPedidos.length} pedido(s) encontrado(s)
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ============ Modal: Ver Cliente (Radix Dialog aninhado — isola do form pai) ============ */}
                  <Dialog open={showViewCliente} onOpenChange={setShowViewCliente}>
                    <DialogContent
                      className="w-full max-w-2xl max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden"
                      onOpenAutoFocus={(e) => e.preventDefault()}
                      data-testid="view-cliente-modal"
                    >
                      {/* Header (fixo) */}
                      <DialogHeader className="px-4 py-3 border-b bg-muted/30 shrink-0 flex-row items-center justify-between space-y-0">
                        <DialogTitle className="font-semibold flex items-center gap-2 min-w-0 text-left">
                          <User className="h-5 w-5 text-primary shrink-0" />
                          <span className="truncate">Cliente: {viewClienteData?.customer?.nome || '...'}</span>
                        </DialogTitle>
                        <Button variant="outline" size="sm" onClick={() => { setShowViewCliente(false); setTimeout(() => handleAbrirEditCliente(), 100); }} data-testid="btn-view-editar">
                          <Edit className="h-4 w-4 mr-1" /> Editar
                        </Button>
                      </DialogHeader>
                      {/* Body (rolável) */}
                      <div className="flex-1 overflow-y-auto p-4 space-y-4">
                          {viewClienteLoading ? (
                            <div className="flex items-center justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                          ) : viewClienteData ? (
                            <>
                              {/* Cards de resumo */}
                              <div className="grid grid-cols-3 gap-3">
                                <div className="border rounded-lg p-3 bg-blue-500/5">
                                  <p className="text-xs text-muted-foreground">Pedidos</p>
                                  <p className="text-lg font-bold text-blue-500">{viewClienteData.totalPedidos}</p>
                                </div>
                                <div className={`border rounded-lg p-3 ${viewClienteData.saldoDevedor > 0 ? 'bg-red-500/5' : 'bg-green-500/5'}`}>
                                  <p className="text-xs text-muted-foreground">Saldo Devedor</p>
                                  <p className={`text-lg font-bold font-mono ${viewClienteData.saldoDevedor > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                    R$ {(viewClienteData.saldoDevedor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </p>
                                </div>
                                <div className="border rounded-lg p-3 bg-muted/30">
                                  <p className="text-xs text-muted-foreground">Tipo</p>
                                  <p className="text-sm font-semibold">{(viewClienteData.customer?.tipo === 'PJ') ? 'PJ' : 'PF'}</p>
                                </div>
                              </div>

                              {/* Dados do cliente */}
                              <div className="border rounded-lg p-3 space-y-2">
                                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Dados Pessoais</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                  <div><span className="text-muted-foreground">Nome: </span><span className="font-medium">{viewClienteData.customer?.nome || '-'}</span></div>
                                  <div><span className="text-muted-foreground">CPF/CNPJ: </span><span className="font-medium">{viewClienteData.customer?.cpf_cnpj || '-'}</span></div>
                                  <div><span className="text-muted-foreground">Telefone: </span><span className="font-medium">{viewClienteData.customer?.telefone || '-'}</span></div>
                                  <div><span className="text-muted-foreground">Email: </span><span className="font-medium">{viewClienteData.customer?.email || '-'}</span></div>
                                </div>
                              </div>

                              {/* Endereço completo */}
                              <div className="border rounded-lg p-3 space-y-2">
                                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Endereço</h4>
                                <div className="text-sm space-y-1">
                                  <div><span className="text-muted-foreground">Logradouro: </span><span className="font-medium">{viewClienteData.customer?.endereco || '-'}</span></div>
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
                                    <div><span className="text-muted-foreground">Bairro: </span><span className="font-medium">{viewClienteData.customer?.bairro || '-'}</span></div>
                                    <div><span className="text-muted-foreground">Cidade: </span><span className="font-medium">{viewClienteData.customer?.cidade || '-'}</span></div>
                                    <div><span className="text-muted-foreground">UF: </span><span className="font-medium">{viewClienteData.customer?.estado || '-'}</span></div>
                                  </div>
                                  <div><span className="text-muted-foreground">CEP: </span><span className="font-medium">{viewClienteData.customer?.cep || '-'}</span></div>
                                </div>
                              </div>

                              {/* Lista de pedidos com status e saldo */}
                              <div className="border rounded-lg p-3 space-y-2">
                                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Pedidos ({viewClienteData.orders.length})</h4>
                                {viewClienteData.orders.length === 0 ? (
                                  <p className="text-sm text-muted-foreground text-center py-4">Nenhum pedido registrado.</p>
                                ) : (
                                  <div className="space-y-2">
                                    {viewClienteData.orders.map(o => {
                                      const total = Number(o.total_com_desconto ?? o.total_venda ?? 0);
                                      const pago = Number(o.total_pago ?? 0);
                                      const saldo = Math.max(0, total - pago);
                                      const cancelado = (o.status || '').toLowerCase() === 'cancelado';
                                      const statusColor = 
                                        o.status === 'Finalizado' || o.status === 'Entregue' ? 'bg-green-500/20 text-green-600' :
                                        o.status === 'Entregue Parcial' ? 'bg-blue-500/20 text-blue-600' :
                                        o.status === 'Aguardando' ? 'bg-yellow-500/20 text-yellow-600' :
                                        cancelado ? 'bg-red-500/20 text-red-600' :
                                        'bg-muted text-muted-foreground';
                                      return (
                                        <div key={o.id} className="flex items-center justify-between border rounded p-2 text-sm gap-2">
                                          <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                                            <span className="font-semibold text-primary shrink-0">{o.numero_pedido}</span>
                                            <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${statusColor}`}>{o.status || '-'}</span>
                                            <span className="text-xs text-muted-foreground shrink-0">
                                              {o.data_pedido ? String(o.data_pedido).split('T')[0].split('-').reverse().join('/') : '-'}
                                            </span>
                                          </div>
                                          <div className="text-right shrink-0">
                                            <div className="text-xs whitespace-nowrap">Total: <span className="font-mono">R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                                            {!cancelado && saldo > 0 && (
                                              <div className="text-xs text-red-500 whitespace-nowrap">Devendo: <span className="font-mono">R$ {saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                                            )}
                                            {!cancelado && saldo <= 0 && (
                                              <div className="text-xs text-green-500">Pago</div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </>
                          ) : (
                            <p className="text-center text-muted-foreground py-8">Não foi possível carregar os dados.</p>
                          )}
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* ============ Modal: Editar Cliente (Radix Dialog aninhado — isola do form pai) ============ */}
                  <Dialog open={showEditCliente} onOpenChange={setShowEditCliente}>
                    <DialogContent
                      className="w-full max-w-lg max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden"
                      onOpenAutoFocus={(e) => e.preventDefault()}
                      data-testid="edit-cliente-modal"
                    >
                      {/* Header (fixo) */}
                      <DialogHeader className="px-4 py-3 border-b bg-muted/30 shrink-0">
                        <DialogTitle className="font-semibold flex items-center gap-2 text-left">
                          <Edit className="h-5 w-5 text-primary" />
                          Editar Cliente
                        </DialogTitle>
                      </DialogHeader>
                      {/* Body (rolável) */}
                      <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        <div className="flex gap-2">
                          <Button type="button" size="sm" variant={editClienteForm.tipo === 'PF' ? 'default' : 'outline'} onClick={() => setEditClienteForm({ ...editClienteForm, tipo: 'PF' })}>Pessoa Física</Button>
                          <Button type="button" size="sm" variant={editClienteForm.tipo === 'PJ' ? 'default' : 'outline'} onClick={() => setEditClienteForm({ ...editClienteForm, tipo: 'PJ' })}>Pessoa Jurídica</Button>
                        </div>
                        <div>
                          <Label className="text-xs">Nome *</Label>
                          <Input value={editClienteForm.nome} onChange={(e) => setEditClienteForm({ ...editClienteForm, nome: e.target.value })} data-testid="edit-cliente-nome" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Telefone</Label>
                            <Input value={editClienteForm.telefone} onChange={(e) => setEditClienteForm({ ...editClienteForm, telefone: formatPhone(e.target.value) })} data-testid="edit-cliente-telefone" />
                          </div>
                          <div>
                            <Label className="text-xs">{editClienteForm.tipo === 'PJ' ? 'CNPJ' : 'CPF'}</Label>
                            <Input value={editClienteForm.cpf_cnpj} onChange={(e) => setEditClienteForm({ ...editClienteForm, cpf_cnpj: formatCPFCNPJ(e.target.value) })} />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Email</Label>
                          <Input value={editClienteForm.email} onChange={(e) => setEditClienteForm({ ...editClienteForm, email: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">CEP</Label>
                          <Input value={editClienteForm.cep} onChange={(e) => setEditClienteForm({ ...editClienteForm, cep: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Endereço</Label>
                          <Input value={editClienteForm.endereco} onChange={(e) => setEditClienteForm({ ...editClienteForm, endereco: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs">Bairro</Label>
                            <Input value={editClienteForm.bairro} onChange={(e) => setEditClienteForm({ ...editClienteForm, bairro: e.target.value })} />
                          </div>
                          <div>
                            <Label className="text-xs">Cidade</Label>
                            <Input value={editClienteForm.cidade} onChange={(e) => setEditClienteForm({ ...editClienteForm, cidade: e.target.value })} />
                          </div>
                          <div>
                            <Label className="text-xs">UF</Label>
                            <Input value={editClienteForm.estado} onChange={(e) => setEditClienteForm({ ...editClienteForm, estado: e.target.value.toUpperCase().slice(0, 2) })} />
                          </div>
                        </div>
                      </div>
                      {/* Footer (fixo) */}
                      <div className="flex justify-end gap-2 px-4 py-3 border-t bg-muted/20 shrink-0">
                        <Button variant="outline" onClick={() => setShowEditCliente(false)} data-testid="btn-cancelar-edit-cliente">Cancelar</Button>
                        <Button onClick={handleSalvarEditCliente} disabled={savingEditCliente} data-testid="btn-salvar-edit-cliente">
                          {savingEditCliente ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                          Salvar
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
              
              {clienteMode === 'novo' && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={novoCliente.tipo === 'PF' ? 'default' : 'outline'} onClick={() => setNovoCliente({ ...novoCliente, tipo: 'PF' })}>Pessoa Física</Button>
                    <Button type="button" size="sm" variant={novoCliente.tipo === 'PJ' ? 'default' : 'outline'} onClick={() => setNovoCliente({ ...novoCliente, tipo: 'PJ' })}>Pessoa Jurídica</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2 flex gap-2">
                      <Input 
                        placeholder={novoCliente.tipo === 'PJ' ? "CNPJ" : "CPF"} 
                        value={novoCliente.cpf}
                        onChange={(e) => setNovoCliente({ ...novoCliente, cpf: formatCPFCNPJ(e.target.value) })}
                      />
                      {novoCliente.tipo === 'PJ' && (
                        <Button type="button" variant="outline" onClick={buscarCNPJ} disabled={buscandoCNPJ}>
                          {buscandoCNPJ ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buscar'}
                        </Button>
                      )}
                    </div>
                    <Input placeholder="Nome *" className="col-span-2" value={novoCliente.nome} onChange={(e) => setNovoCliente({ ...novoCliente, nome: e.target.value })} />
                    <Input placeholder="Telefone" value={novoCliente.telefone} onChange={(e) => setNovoCliente({ ...novoCliente, telefone: formatPhone(e.target.value) })} />
                    <Input placeholder="Email" value={novoCliente.email} onChange={(e) => setNovoCliente({ ...novoCliente, email: e.target.value })} />
                    <div className="flex gap-2">
                      <Input placeholder="CEP" value={novoCliente.cep} onChange={(e) => setNovoCliente({ ...novoCliente, cep: e.target.value.replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2').slice(0, 9) })} />
                      <Button type="button" variant="outline" size="icon" onClick={buscarCEP} disabled={buscandoCEP || novoCliente.cep.replace(/\D/g, '').length !== 8}>
                        {buscandoCEP ? <Loader2 className="h-4 w-4 animate-spin" /> : '🔍'}
                      </Button>
                    </div>
                    <Select value={novoCliente.estado} onValueChange={(v) => setNovoCliente({ ...novoCliente, estado: v })}>
                      <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
                      <SelectContent>{ESTADOS_BRASIL.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={novoCliente.cidade} onValueChange={(v) => setNovoCliente({ ...novoCliente, cidade: v })} disabled={!novoCliente.estado || loadingCidadesNovoCliente}>
                      <SelectTrigger><SelectValue placeholder={loadingCidadesNovoCliente ? "Carregando..." : "Cidade"} /></SelectTrigger>
                      <SelectContent>{cidadesNovoCliente.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input placeholder="Bairro" value={novoCliente.bairro} onChange={(e) => setNovoCliente({ ...novoCliente, bairro: e.target.value })} />
                    <Input placeholder="Endereço" className="col-span-2" value={novoCliente.endereco} onChange={(e) => setNovoCliente({ ...novoCliente, endereco: e.target.value })} />
                  </div>
                  {/* Botão para salvar o novo cliente */}
                  <Button 
                    type="button" 
                    className="w-full" 
                    onClick={handleSalvarNovoCliente}
                    disabled={!novoCliente.nome || salvandoCliente}
                  >
                    {salvandoCliente ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Salvando...</> : '✓ Salvar Cliente e Usar no Pedido'}
                  </Button>
                </div>
              )}
              
              {clienteMode === 'avulso' && (
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Nome do cliente" value={formData.cliente_avulso.nome} onChange={(e) => setFormData({ ...formData, cliente_avulso: { ...formData.cliente_avulso, nome: e.target.value } })} />
                  <Input placeholder="Telefone" value={formData.cliente_avulso.telefone} onChange={(e) => setFormData({ ...formData, cliente_avulso: { ...formData.cliente_avulso, telefone: formatPhone(e.target.value) } })} />
                  <Input placeholder="Endereço (opcional)" className="col-span-2" value={formData.cliente_avulso.endereco} onChange={(e) => setFormData({ ...formData, cliente_avulso: { ...formData.cliente_avulso, endereco: e.target.value } })} />
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* 4. Produtos */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Package className="h-4 w-4 text-primary" />
                <span className="font-semibold text-sm">Produtos</span>
                <div className="flex gap-1 ml-auto">
                  <Button type="button" size="sm" variant={produtoMode === 'catalogo' ? 'default' : 'outline'} onClick={() => setProdutoMode('catalogo')}>Catálogo</Button>
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="outline"
                    className="bg-emerald-500/10 border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/20"
                    onClick={() => setShowProductModal(true)}
                    data-testid="btn-novo-produto-pedido"
                  >
                    <Plus className="h-3 w-3 mr-1" />Novo Produto
                  </Button>
                </div>
              </div>
              
              {produtoMode === 'catalogo' && (
                <div className="space-y-3">
                  <div className="relative">
                    <Input
                      ref={produtoSearchRef}
                      placeholder="Digite para pesquisar produto..."
                      value={produtoSearch}
                      onChange={(e) => { setProdutoSearch(e.target.value); setShowProdutoDropdown(true); setHighlightedIndex(0); }}
                      onFocus={() => { setShowProdutoDropdown(true); setHighlightedIndex(0); }}
                      onKeyDown={(e) => {
                        if (!showProdutoDropdown) return;
                        
                        const totalItems = filteredProducts.length + 1; // +1 para "Produto Avulso"
                        
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          const newIndex = Math.min(highlightedIndex + 1, totalItems - 1);
                          setHighlightedIndex(newIndex);
                          // Scroll para o item
                          const container = produtoListRef.current;
                          if (container) {
                            const item = container.children[newIndex];
                            if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                          }
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          const newIndex = Math.max(highlightedIndex - 1, 0);
                          setHighlightedIndex(newIndex);
                          // Scroll para o item
                          const container = produtoListRef.current;
                          if (container) {
                            const item = container.children[newIndex];
                            if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                          }
                        } else if (e.key === 'Enter' && highlightedIndex >= 0) {
                          e.preventDefault();
                          if (highlightedIndex === 0) {
                            // Produto Avulso
                            handleProdutoChange('avulso');
                          } else {
                            const selected = filteredProducts[highlightedIndex - 1];
                            if (selected) {
                              handleProdutoChange(selected.id);
                            }
                          }
                          setShowProdutoDropdown(false);
                        } else if (e.key === 'Escape') {
                          setShowProdutoDropdown(false);
                        }
                      }}
                    />
                    {showProdutoDropdown && produtoSearch && (
                      <div ref={produtoListRef} className="absolute z-[100] w-full mt-1 bg-background border rounded-lg shadow-2xl max-h-[250px] overflow-y-auto">
                        <div
                          className={`px-4 py-3 cursor-pointer border-b ${highlightedIndex === 0 ? 'bg-primary/20' : 'hover:bg-muted/50'}`}
                          onClick={() => handleProdutoChange('avulso')}
                        >
                          <div className="font-semibold text-primary">➕ Produto Avulso</div>
                          <div className="text-sm text-muted-foreground">Adicionar item não cadastrado</div>
                        </div>
                        {filteredProducts.map((p, idx) => (
                          <div
                            key={p.id}
                            className={`px-4 py-3 cursor-pointer ${highlightedIndex === idx + 1 ? 'bg-primary/20' : 'hover:bg-muted/50'}`}
                            onClick={() => handleProdutoChange(p.id)}
                          >
                            <div className="font-semibold">{p.nome}</div>
                            <div className="text-sm text-muted-foreground">{p.categoria || 'Sem categoria'} • R$ {formatMoney(p.preco_base || 0)}</div>
                          </div>
                        ))}
                        {filteredProducts.length === 0 && <div className="px-4 py-3 text-muted-foreground">Nenhum produto encontrado</div>}
                      </div>
                    )}
                  </div>
                  
                  {produtoAtual.produto_id && (
                    <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                      <div className="font-medium">{produtoAtual.nome}</div>
                      {/* Mostrar info de madeira se existir */}
                      {produtoAtual.volume_m3 && produtoAtual.tipo_madeira_nome && (
                        <div className="text-xs p-2 bg-amber-500/10 border border-amber-500/30 rounded">
                          <span className="text-amber-600 font-medium">🪵 Madeira:</span>{' '}
                          {produtoAtual.tipo_madeira_nome} • {produtoAtual.volume_m3.toFixed(4)} m³ × R$ {formatMoney(produtoAtual.tipo_madeira_preco || 0)}/m³ = {' '}
                          <strong>R$ {formatMoney(produtoAtual.volume_m3 * (produtoAtual.tipo_madeira_preco || 0))}</strong>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs">Quantidade</Label>
                          <Input 
                            ref={quantityInputRef}
                            type="number" 
                            step="0.01"
                            min="0.01" 
                            value={produtoAtual.quantidade === 0 ? '' : produtoAtual.quantidade} 
                            onChange={(e) => {
                              const val = e.target.value;
                              setProdutoAtual({ ...produtoAtual, quantidade: val === '' ? 0 : parseFloat(val) || 0 });
                            }}
                            onBlur={(e) => {
                              // Garantir mínimo de 0.01 ao sair do campo
                              if (!e.target.value || parseFloat(e.target.value) < 0.01) {
                                setProdutoAtual({ ...produtoAtual, quantidade: 1 });
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (editingProdutoIdx !== null) {
                                  handleSaveEditProduto();
                                } else {
                                  handleAddProduto();
                                }
                              }
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Preço Unit.</Label>
                          <Input 
                            type="number" 
                            step="0.01" 
                            value={produtoAtual.preco_unitario} 
                            onChange={(e) => setProdutoAtual({ ...produtoAtual, preco_unitario: parseFloat(e.target.value) || 0 })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (editingProdutoIdx !== null) {
                                  handleSaveEditProduto();
                                } else {
                                  handleAddProduto();
                                }
                              }
                            }}
                          />
                        </div>
                        <div className="flex items-end gap-2">
                          {editingProdutoIdx !== null ? (
                            <>
                              <Button type="button" className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleSaveEditProduto}>
                                <Edit className="h-4 w-4 mr-1" />Salvar
                              </Button>
                              <Button type="button" variant="outline" onClick={handleCancelEditProduto}>
                                Cancelar
                              </Button>
                            </>
                          ) : (
                            <Button type="button" className="w-full" onClick={handleAddProduto}><Plus className="h-4 w-4 mr-1" />Adicionar</Button>
                          )}
                        </div>
                      </div>
                      {/* Mostrar total do item antes de adicionar */}
                      {produtoAtual.quantidade > 0 && produtoAtual.preco_unitario > 0 && (
                        <div className="mt-2 p-2 bg-primary/10 rounded-lg text-sm">
                          <div className="flex justify-between">
                            <span>{produtoAtual.quantidade}x R$ {formatMoney(produtoAtual.preco_unitario)}</span>
                            <span className="font-bold">Total: R$ {formatMoney(produtoAtual.quantidade * produtoAtual.preco_unitario)}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* ========== SEÇÃO DE VARIAÇÕES ========== */}
                      {produtoAtual.produto_id && (variacoesProduto.length > 0 || loadingVariacoes) && (
                        <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-sm font-medium text-purple-700">Variações Disponíveis</Label>
                            {loadingVariacoes && <Loader2 className="h-4 w-4 animate-spin text-purple-600" />}
                          </div>
                          
                          {!loadingVariacoes && variacoesProduto.length > 0 && (
                            <div className="space-y-2">
                              {variacoesProduto.map((variacao) => {
                                const isSelected = variacoesSelecionadas.some(v => v.id === variacao.id);
                                return (
                                  <div
                                    key={variacao.id}
                                    onClick={() => handleToggleVariacao(variacao)}
                                    className={`p-2 rounded border cursor-pointer transition-all ${
                                      isSelected 
                                        ? 'bg-purple-500/20 border-purple-500' 
                                        : 'bg-background border-muted hover:border-purple-500/50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                          isSelected ? 'bg-purple-600 border-purple-600' : 'border-muted-foreground'
                                        }`}>
                                          {isSelected && <span className="text-white text-xs">✓</span>}
                                        </div>
                                        <span className="font-medium text-sm">{variacao.nome}</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                                          variacao.tipo === 'obrigatoria' ? 'bg-red-100 text-red-700' :
                                          variacao.tipo === 'opcional' ? 'bg-blue-100 text-blue-700' :
                                          'bg-green-100 text-green-700'
                                        }`}>{variacao.tipo}</span>
                                      </div>
                                      <div className="text-xs text-right">
                                        {variacao.valor_venda_adicional > 0 && (
                                          <span className="text-green-600 font-medium">+R$ {formatMoney(variacao.valor_venda_adicional)}</span>
                                        )}
                                      </div>
                                    </div>
                                    {variacao.descricao && (
                                      <p className="text-xs text-muted-foreground mt-1 ml-6">{variacao.descricao}</p>
                                    )}
                                  </div>
                                );
                              })}
                              
                              {/* Resumo das variações selecionadas */}
                              {variacoesSelecionadas.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-purple-500/30 text-xs">
                                  <div className="flex justify-between text-purple-700">
                                    <span>{variacoesSelecionadas.length} variação(ões) selecionada(s)</span>
                                    <span className="font-medium">
                                      +R$ {formatMoney(variacoesSelecionadas.reduce((sum, v) => sum + (v.valor_venda_adicional || 0), 0))}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {!loadingVariacoes && variacoesProduto.length === 0 && (
                            <p className="text-xs text-muted-foreground">Nenhuma variação cadastrada</p>
                          )}
                        </div>
                      )}
                      {/* ========== FIM SEÇÃO VARIAÇÕES ========== */}
                    </div>
                  )}
                </div>
              )}
              
              {produtoMode === 'avulso' && (
                <div className="p-3 bg-muted/50 rounded-lg space-y-3 max-h-[400px] overflow-y-auto">
                  <div className="text-sm font-medium mb-2">Cadastrar Novo Produto</div>
                  
                  {/* Nome e Descrição */}
                  <div className="space-y-2">
                    <Input placeholder="Nome do produto *" value={novoProduto.nome} onChange={(e) => setNovoProduto({ ...novoProduto, nome: e.target.value })} />
                    <Textarea placeholder="Descrição do produto" rows={2} value={novoProduto.descricao || ''} onChange={(e) => setNovoProduto({ ...novoProduto, descricao: e.target.value })} />
                  </div>
                  
                  {/* Categoria e Unidade */}
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={novoProduto.categoria || 'none'} onValueChange={(v) => setNovoProduto({ ...novoProduto, categoria: v === 'none' ? '' : v })}>
                      <SelectTrigger><SelectValue placeholder="Categoria" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem categoria</SelectItem>
                        {productCategories?.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={novoProduto.unidade || 'UN'} onValueChange={(v) => setNovoProduto({ ...novoProduto, unidade: v })}>
                      <SelectTrigger><SelectValue placeholder="Unidade" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UN">UN (Unidade)</SelectItem>
                        <SelectItem value="ML">Metro Linear</SelectItem>
                        <SelectItem value="M2">m² (Metro Quadrado)</SelectItem>
                        <SelectItem value="M3">m³ (Metro Cúbico)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Preço, Custo e Estoque */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Preço Venda *</Label>
                      <Input type="number" step="0.01" placeholder="0.00" value={novoProduto.preco} onChange={(e) => setNovoProduto({ ...novoProduto, preco: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">Custo</Label>
                      <Input type="number" step="0.01" placeholder="0.00" value={novoProduto.custo} onChange={(e) => setNovoProduto({ ...novoProduto, custo: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">Estoque</Label>
                      <Input type="number" placeholder="0" value={novoProduto.estoque_minimo || ''} onChange={(e) => setNovoProduto({ ...novoProduto, estoque_minimo: e.target.value })} />
                    </div>
                  </div>
                  
                  {/* NCM */}
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={['44182100', '44181100', '94036000'].includes(novoProduto.ncm) ? novoProduto.ncm : 'custom'} onValueChange={(v) => v !== 'custom' && setNovoProduto({ ...novoProduto, ncm: v })}>
                      <SelectTrigger><SelectValue placeholder="NCM" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="44182100">44182100 - Portas/Forras</SelectItem>
                        <SelectItem value="44181100">44181100 - Janelas</SelectItem>
                        <SelectItem value="94036000">94036000 - Móveis</SelectItem>
                        <SelectItem value="custom">Digitar...</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input placeholder="NCM manual" value={novoProduto.ncm || ''} onChange={(e) => setNovoProduto({ ...novoProduto, ncm: e.target.value })} />
                  </div>
                  
                  {/* Observações */}
                  <Textarea placeholder="Observações (aparece no PDF)" rows={2} value={novoProduto.observacoes || ''} onChange={(e) => setNovoProduto({ ...novoProduto, observacoes: e.target.value })} />
                  
                  {/* Controle de Madeira */}
                  <div className="border-t pt-3">
                    <div className="text-xs font-medium text-amber-600 mb-2">🪵 Controle de Estoque de Madeira</div>
                    
                    {/* Tipo de Madeira */}
                    <div className="mb-3">
                      <Label className="text-xs">Tipo de Madeira</Label>
                      <Select 
                        value={novoProduto.tipo_madeira_id || ''}
                        onValueChange={(v) => {
                          const tipoId = v === 'none' ? '' : v;
                          const pecas = novoProduto.pecas_madeira || [];
                          const custoMadeira = calcularCustoMadeira(pecas, tipoId);
                          const maoDeObra = parseFloat(novoProduto.mao_de_obra) || 0;
                          const pintura = parseFloat(novoProduto.pintura) || 0;
                          const volumeTotal = calcularVolumeTotal(pecas);
                          setNovoProduto({ 
                            ...novoProduto, 
                            tipo_madeira_id: tipoId,
                            volume_m3: volumeTotal > 0 ? volumeTotal.toFixed(6) : novoProduto.volume_m3,
                            custo: (custoMadeira + maoDeObra + pintura).toFixed(2)
                          });
                        }}
                      >
                        <SelectTrigger className="text-xs">
                          <SelectValue placeholder="Selecione o tipo de madeira" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nenhum</SelectItem>
                          {tiposMadeira.map(tipo => (
                            <SelectItem key={tipo.id} value={tipo.id}>
                              {tipo.nome} - R$ {formatMoney(tipo.preco_m3)}/m³
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Peças de Madeira - aparece quando tipo de madeira é selecionado */}
                    {novoProduto.tipo_madeira_id && (
                      <div className="space-y-3 p-3 bg-amber-500/5 border border-amber-500/30 rounded-lg mb-3">
                        <div className="text-sm font-medium text-amber-700">Peças de Madeira</div>
                        
                        {/* Formulário para adicionar peça */}
                        <div className="grid grid-cols-5 gap-2">
                          <div>
                            <Label className="text-xs">Comp. (cm)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="210"
                              value={novaPecaMadeira.comprimento}
                              onChange={(e) => setNovaPecaMadeira({ ...novaPecaMadeira, comprimento: e.target.value })}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPecaMadeira())}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Larg. (cm)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="13"
                              value={novaPecaMadeira.largura}
                              onChange={(e) => setNovaPecaMadeira({ ...novaPecaMadeira, largura: e.target.value })}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPecaMadeira())}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Esp. (cm)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="3.5"
                              value={novaPecaMadeira.espessura}
                              onChange={(e) => setNovaPecaMadeira({ ...novaPecaMadeira, espessura: e.target.value })}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPecaMadeira())}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Qtd</Label>
                            <Input
                              type="number"
                              min="1"
                              placeholder="1"
                              value={novaPecaMadeira.quantidade}
                              onChange={(e) => setNovaPecaMadeira({ ...novaPecaMadeira, quantidade: e.target.value })}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPecaMadeira())}
                            />
                          </div>
                          <div className="flex items-end">
                            <Button type="button" size="sm" onClick={handleAddPecaMadeira} className="w-full">
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Lista de peças adicionadas */}
                        {(novoProduto.pecas_madeira || []).length > 0 && (
                          <div className="space-y-1 mt-2">
                            {novoProduto.pecas_madeira.map((peca, idx) => (
                              <div key={peca.id || idx} className="flex items-center justify-between text-xs bg-background p-2 rounded border">
                                <span>
                                  <strong>{peca.quantidade}x</strong> {peca.comprimento}cm × {peca.largura}cm × {peca.espessura}cm
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className="text-amber-600 font-medium">
                                    {calcularVolumePeca(peca).toFixed(6)} m³
                                  </span>
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-6 w-6 text-destructive"
                                    onClick={() => handleRemovePecaMadeira(peca.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Subtotal de volume */}
                        {(novoProduto.pecas_madeira || []).length > 0 && (
                          <div className="text-xs text-right border-t pt-2 mt-2">
                            <span className="text-muted-foreground">Volume Total: </span>
                            <span className="font-bold text-amber-600">
                              {calcularVolumeTotal(novoProduto.pecas_madeira).toFixed(6)} m³
                            </span>
                            <span className="text-muted-foreground"> = </span>
                            <span className="font-bold text-green-600">
                              R$ {formatMoney(calcularCustoMadeira(novoProduto.pecas_madeira, novoProduto.tipo_madeira_id))}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Mão de Obra e Pintura */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Mão de Obra (R$)</Label>
                        <Input 
                          type="number" 
                          step="0.01" 
                          placeholder="0.00" 
                          value={novoProduto.mao_de_obra || ''} 
                          onChange={(e) => {
                            const maoDeObra = parseFloat(e.target.value) || 0;
                            const pecas = novoProduto.pecas_madeira || [];
                            const custoMadeira = calcularCustoMadeira(pecas, novoProduto.tipo_madeira_id);
                            const pintura = parseFloat(novoProduto.pintura) || 0;
                            const custoTotal = (custoMadeira + maoDeObra + pintura).toFixed(2);
                            setNovoProduto({ 
                              ...novoProduto, 
                              mao_de_obra: e.target.value,
                              custo: custoTotal
                            });
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Pintura (R$)</Label>
                        <Input 
                          type="number" 
                          step="0.01" 
                          placeholder="0.00" 
                          value={novoProduto.pintura || ''} 
                          onChange={(e) => {
                            const pintura = parseFloat(e.target.value) || 0;
                            const pecas = novoProduto.pecas_madeira || [];
                            const custoMadeira = calcularCustoMadeira(pecas, novoProduto.tipo_madeira_id);
                            const maoDeObra = parseFloat(novoProduto.mao_de_obra) || 0;
                            const custoTotal = (custoMadeira + maoDeObra + pintura).toFixed(2);
                            setNovoProduto({ 
                              ...novoProduto, 
                              pintura: e.target.value,
                              custo: custoTotal
                            });
                          }}
                        />
                      </div>
                    </div>
                    
                    {/* Resumo do Custo Total */}
                    {novoProduto.tipo_madeira_id && (novoProduto.pecas_madeira || []).length > 0 && (
                      <div className="mt-3 text-xs p-2 bg-green-500/10 border border-green-500/30 rounded">
                        <strong>Custo Total do Produto:</strong><br/>
                        <span>Madeira ({calcularVolumeTotal(novoProduto.pecas_madeira).toFixed(4)} m³): R$ {formatMoney(calcularCustoMadeira(novoProduto.pecas_madeira, novoProduto.tipo_madeira_id))}</span>
                        {parseFloat(novoProduto.mao_de_obra) > 0 && <><br/>+ Mão de Obra: R$ {formatMoney(novoProduto.mao_de_obra)}</>}
                        {parseFloat(novoProduto.pintura) > 0 && <><br/>+ Pintura: R$ {formatMoney(novoProduto.pintura)}</>}
                        <br/>
                        <span className="text-green-600 font-bold text-sm">
                          = Total: R$ {novoProduto.custo || '0,00'}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Botão Adicionar */}
                  <Button type="button" className="w-full" onClick={handleAddProduto}>
                    <Plus className="h-4 w-4 mr-1" />
                    Salvar no Catálogo e Adicionar ao Pedido
                  </Button>
                </div>
              )}
              
              {/* Lista de produtos */}
              {formData.produtos.length > 0 && (
                <div className="mt-4 border rounded-lg">
                  <div className="px-3 py-2 bg-muted/50 border-b text-sm font-medium">Itens do Pedido</div>
                  {formData.produtos.map((item, idx) => (
                    <div key={idx} className="px-3 py-2 border-b last:border-b-0 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">{item.nome}</span>
                          <span className="text-sm text-muted-foreground ml-2">{item.quantidade}x R$ {formatMoney(item.preco_unitario || 0)}</span>
                          {item.volume_m3 && item.tipo_madeira_nome && (
                            <div className="text-xs text-amber-600 mt-0.5">
                              🪵 {item.tipo_madeira_nome} • {(item.volume_m3 * item.quantidade).toFixed(4)} m³
                            </div>
                          )}
                          {/* Mostrar variações selecionadas */}
                          {item.variacoes_selecionadas && item.variacoes_selecionadas.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {item.variacoes_selecionadas.map((v, vIdx) => (
                                <span key={vIdx} className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                                  {v.nome} {v.valor_adicional > 0 && `+R$ ${formatMoney(v.valor_adicional)}`}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">R$ {formatMoney(item.quantidade * item.preco_unitario)}</span>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => handleEditProduto(idx)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleRemoveProduto(idx)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {/* Observação do produto */}
                      <Input 
                        placeholder="Observação do produto (ex: cor, medida, acabamento...)" 
                        className="text-xs"
                        value={item.observacoes || ''} 
                        onChange={(e) => {
                          const novosProdutos = [...formData.produtos];
                          novosProdutos[idx] = { ...novosProdutos[idx], observacoes: e.target.value };
                          setFormData({ ...formData, produtos: novosProdutos });
                        }}
                      />
                    </div>
                  ))}
                  <div className="px-3 py-2 bg-muted/30 flex justify-between font-semibold">
                    <span>Total</span>
                    <span>R$ {formatMoney(totalVenda)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* 5. Desconto */}
          {formData.produtos.length > 0 && (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <Select value={formData.desconto_tipo || 'none'} onValueChange={(v) => setFormData({ ...formData, desconto_tipo: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Tipo Desconto" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem desconto</SelectItem>
                    <SelectItem value="percentual">Percentual (%)</SelectItem>
                    <SelectItem value="valor">Valor Fixo (R$)</SelectItem>
                  </SelectContent>
                </Select>
                {formData.desconto_tipo && (
                  <Input
                    type="number"
                    step="0.01"
                    placeholder={formData.desconto_tipo === 'percentual' ? '% desconto' : 'R$ desconto'}
                    value={formData.desconto_valor}
                    onChange={(e) => setFormData({ ...formData, desconto_valor: e.target.value })}
                  />
                )}
                {formData.desconto_tipo && (
                  <div className="flex items-center justify-end font-semibold">
                    Total: R$ {formatMoney(totalComDesconto)}
                  </div>
                )}
              </div>
              {/* Mostrar equivalência: se foi % mostra valor, se foi valor mostra % */}
              {formData.desconto_tipo && formData.desconto_valor && parseFloat(formData.desconto_valor) > 0 && (
                <div className="text-xs text-red-500 text-right font-medium">
                  {formData.desconto_tipo === 'percentual' ? (
                    <>Desconto de {formData.desconto_valor}% = <strong>R$ {formatMoney(valorDesconto)}</strong></>
                  ) : (
                    <>Desconto de R$ {formatMoney(formData.desconto_valor)} = <strong>{totalVenda > 0 ? ((parseFloat(formData.desconto_valor) / totalVenda) * 100).toFixed(2) : 0}%</strong></>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 5b. Analisar proposta com IA (usa custos dos produtos do pedido) */}
          {formData.produtos.length > 0 && (
            <div className="flex justify-end">
              <AiAnalysisTrigger
                label="Analisar esta proposta com IA"
                variant="default"
                size="default"
                className="bg-gradient-to-r from-purple-600 to-primary hover:from-purple-700 hover:to-primary/90 text-white"
                getInitialValues={() => {
                  // Detecta cartão nos pagamentos já lançados para pré-preencher bandeira/parcelas
                  const pagCartao = (formData.pagamentos || []).find(p =>
                    (p.forma_pagamento === 'Cartão Crédito' || p.forma_pagamento === 'Crédito'));
                  const pagAvista = (formData.pagamentos || []).find(p =>
                    ['PIX', 'Pix', 'Dinheiro', 'Transferência', 'Débito', 'Cartão Débito'].includes(p.forma_pagamento));
                  const formaMap = {
                    'PIX': 'pix', 'Pix': 'pix',
                    'Dinheiro': 'dinheiro',
                    'Transferência': 'transferencia', 'Transferencia': 'transferencia',
                    'Débito': 'debito', 'Cartão Débito': 'debito',
                  };
                  return {
                    valorOriginal: totalVenda,
                    valorOferecido: totalComDesconto,
                    custoProdutos: totalCusto,
                    valorAvista: pagAvista?.valor ?? '',
                    formaAvista: formaMap[pagAvista?.forma_pagamento] || 'pix',
                    parcelas: pagCartao?.parcelas || 10,
                    bandeira: (pagCartao?.bandeira || 'mastercard').toLowerCase(),
                    pedidoId: editingOrder?.id,
                    clienteId: formData.cliente_id,
                  };
                }}
              />
            </div>
          )}
          
          {/* 6. Pagamentos */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="h-4 w-4 text-primary" />
                <span className="font-semibold text-sm">Pagamentos</span>
                <span className="text-xs text-muted-foreground ml-auto">
                  Restante: R$ {formatMoney(saldoRestante)}
                </span>
              </div>
              
              <div className="space-y-3 mb-3">
                {/* Linha 1: Forma de pagamento e Valor */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Select value={pagamentoAtual.forma_pagamento} onValueChange={(v) => {
                    // Se selecionou cartão, auto-selecionar a conta de cartão
                    let contaId = pagamentoAtual.conta_id;
                    if (v === 'Cartão Crédito' || v === 'Cartão Débito') {
                      const contaCartao = bankAccounts?.find(acc => acc.conta_cartao);
                      if (contaCartao) {
                        contaId = contaCartao.id;
                      }
                    }
                    setPagamentoAtual({ ...pagamentoAtual, forma_pagamento: v, bandeira: '', parcelas: 1, nsu: '', conta_id: contaId });
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PIX">PIX</SelectItem>
                      <SelectItem value="Dinheiro">Dinheiro</SelectItem>
                      <SelectItem value="Cartão Crédito">Cartão Crédito</SelectItem>
                      <SelectItem value="Cartão Débito">Cartão Débito</SelectItem>
                      <SelectItem value="Boleto">Boleto</SelectItem>
                      <SelectItem value="Transferência">Transferência</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Valor"
                    value={pagamentoAtual.valor}
                    onChange={(e) => setPagamentoAtual({ ...pagamentoAtual, valor: e.target.value })}
                  />
                  <Input
                    type="date"
                    value={pagamentoAtual.data_pagamento || ''}
                    onChange={(e) => setPagamentoAtual({ ...pagamentoAtual, data_pagamento: e.target.value })}
                    title="Data do pagamento"
                  />
                  <Select value={pagamentoAtual.conta_id || 'sem-conta'} onValueChange={(v) => setPagamentoAtual({ ...pagamentoAtual, conta_id: v === 'sem-conta' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="Conta" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sem-conta">Sem conta</SelectItem>
                      {bankAccounts?.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>{acc.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Linha 2: Campos específicos de cartão */}
                {(pagamentoAtual.forma_pagamento === 'Cartão Crédito' || pagamentoAtual.forma_pagamento === 'Cartão Débito') && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-2 border rounded-lg bg-muted/30">
                    <Select value={pagamentoAtual.bandeira || ''} onValueChange={(v) => setPagamentoAtual({ ...pagamentoAtual, bandeira: v })}>
                      <SelectTrigger><SelectValue placeholder="Bandeira *" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="VISA">VISA</SelectItem>
                        <SelectItem value="MASTERCARD">MASTERCARD</SelectItem>
                        <SelectItem value="ELO">ELO</SelectItem>
                        <SelectItem value="AMEX">AMEX</SelectItem>
                        <SelectItem value="HIPERCARD">HIPERCARD</SelectItem>
                      </SelectContent>
                    </Select>
                    {pagamentoAtual.forma_pagamento === 'Cartão Crédito' && (
                      <Select value={String(pagamentoAtual.parcelas || 1)} onValueChange={(v) => setPagamentoAtual({ ...pagamentoAtual, parcelas: parseInt(v) })}>
                        <SelectTrigger><SelectValue placeholder="Parcelas" /></SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                            <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Input
                      placeholder="NSU"
                      value={pagamentoAtual.nsu || ''}
                      onChange={(e) => setPagamentoAtual({ ...pagamentoAtual, nsu: e.target.value })}
                    />
                  </div>
                )}
                
                {/* Linha 3: Botão adicionar */}
                <Button type="button" onClick={handleAddPagamento} className="w-full"><Plus className="h-4 w-4 mr-1" />Adicionar Pagamento</Button>
              </div>
              
              {formData.pagamentos.length > 0 && (
                <div className="border rounded-lg">
                  {formData.pagamentos.map((pag, idx) => {
                    // Buscar nome da conta bancária
                    const contaBancaria = pag.conta_id ? bankAccounts?.find(acc => acc.id === pag.conta_id) : null;
                    
                    // Formatar data do pagamento (usa helper que evita bug de timezone)
                    let dataFormatada = null;
                    if (pag.data_pagamento) {
                      dataFormatada = formatDateDisplay(pag.data_pagamento);
                    }
                    
                    return (
                    <div key={idx} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2 border-b last:border-b-0">
                      <div className="flex flex-col min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{pag.forma_pagamento}</span>
                          {pag.bandeira && <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">{pag.bandeira}</span>}
                          {pag.parcelas > 1 && <span className="text-xs text-muted-foreground">{pag.parcelas}x</span>}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                          {dataFormatada && (
                            <span className="text-blue-600">
                              {dataFormatada}
                            </span>
                          )}
                          {contaBancaria && (
                            <span className="bg-muted px-1.5 py-0.5 rounded truncate max-w-[140px]">
                              {contaBancaria.nome}
                            </span>
                          )}
                          {pag.nsu && <span>NSU: {pag.nsu}</span>}
                          {pag.observacao && <span>({pag.observacao})</span>}
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
                        <span className="font-semibold whitespace-nowrap">R$ {formatMoney(pag.valor || 0)}</span>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleRemovePagamento(idx)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )})}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* 7. Observações */}
          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea
              placeholder="Observações do pedido..."
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
            />
          </div>
          
          {/* 8. Emitir NF-e */}
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.emitir_nfe}
              onCheckedChange={(checked) => setFormData({ ...formData, emitir_nfe: checked })}
            />
            <Label>Emitir NF-e automaticamente ao faturar</Label>
          </div>
          
          {/* 9. Parcelamento - Contas a Receber */}
          {saldoRestante > 0 && (
            <Card className="border-amber-500/50 bg-amber-500/5">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Receipt className="h-4 w-4 text-amber-600" />
                  <span className="font-semibold text-sm text-amber-700">
                    {editingOrder ? 'Parcelar Valor Restante' : 'Parcelamento (Contas a Receber)'}
                  </span>
                  <Switch
                    checked={parcelamentoAtivo}
                    onCheckedChange={(checked) => {
                      setParcelamentoAtivo(checked);
                      if (!checked) setParcelasGeradas([]);
                    }}
                    className="ml-auto"
                  />
                </div>
                
                {parcelamentoAtivo && (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Gera parcelas automáticas no Contas a Receber para o saldo restante de <strong>R$ {formatMoney(saldoRestante)}</strong>
                    </p>
                    
                    {/* Valor de Entrada */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Entrada (opcional)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="R$ 0,00"
                          value={formData.valor_entrada}
                          onChange={(e) => setFormData({ ...formData, valor_entrada: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Nº de Parcelas</Label>
                        <Select 
                          value={String(formData.num_parcelas)} 
                          onValueChange={(v) => setFormData({ ...formData, num_parcelas: parseInt(v) })}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                              <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Modo de data */}
                    <div>
                      <Label className="text-xs">Data da 1ª parcela</Label>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <Button
                          type="button"
                          variant={formData.modo_data_parcelas === 'automatico' ? 'default' : 'outline'}
                          size="sm"
                          className="text-xs"
                          onClick={() => setFormData({ ...formData, modo_data_parcelas: 'automatico', primeira_parcela_data: '' })}
                        >
                          <CalendarClock className="h-3 w-3 mr-1" />
                          Automático (30 dias)
                        </Button>
                        <Button
                          type="button"
                          variant={formData.modo_data_parcelas === 'manual' ? 'default' : 'outline'}
                          size="sm"
                          className="text-xs"
                          onClick={() => setFormData({ ...formData, modo_data_parcelas: 'manual' })}
                        >
                          <Calendar className="h-3 w-3 mr-1" />
                          Definir Data
                        </Button>
                      </div>
                    </div>
                    
                    {formData.modo_data_parcelas === 'manual' && (
                      <div>
                        <Label className="text-xs">Data 1ª Parcela</Label>
                        <Input
                          type="date"
                          value={formData.primeira_parcela_data}
                          onChange={(e) => setFormData({ ...formData, primeira_parcela_data: e.target.value })}
                        />
                      </div>
                    )}
                    
                    {/* Parcelas Editáveis */}
                    {formData.parcelas_customizadas.length > 0 && (
                      <div className="border rounded-lg bg-white/50 p-2 space-y-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-muted-foreground">Parcelas (editáveis):</span>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 text-xs text-blue-600"
                            onClick={handleRecalcularParcelas}
                          >
                            Recalcular
                          </Button>
                        </div>
                        {parseFloat(formData.valor_entrada) > 0 && (
                          <div className="flex items-center gap-2 text-xs py-1 px-2 bg-green-50 rounded border border-green-200">
                            <span className="text-green-700 font-medium flex-1">Entrada</span>
                            <span className="font-semibold text-green-700">R$ {formatMoney(parseFloat(formData.valor_entrada))}</span>
                          </div>
                        )}
                        {formData.parcelas_customizadas.map((parcela, idx) => (
                          <div key={idx} className="flex items-center gap-2 py-1 border-b last:border-b-0">
                            <span className="text-xs font-medium w-16 shrink-0">
                              {parcela.numero}/{formData.num_parcelas}
                            </span>
                            <Input
                              type="date"
                              className="h-7 text-xs flex-1"
                              value={parcela.data_vencimento}
                              onChange={(e) => handleUpdateParcela(idx, 'data_vencimento', e.target.value)}
                            />
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">R$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                className="h-7 text-xs w-24"
                                value={parcela.valor}
                                onChange={(e) => handleUpdateParcela(idx, 'valor', parseFloat(e.target.value) || 0)}
                              />
                            </div>
                          </div>
                        ))}
                        {/* Total das parcelas */}
                        <div className="flex justify-between text-xs pt-2 border-t font-semibold">
                          <span>Total das parcelas:</span>
                          <span className={
                            Math.abs(formData.parcelas_customizadas.reduce((s, p) => s + p.valor, 0) - (saldoRestante - (parseFloat(formData.valor_entrada) || 0))) > 0.01 
                              ? 'text-red-600' 
                              : 'text-green-600'
                          }>
                            R$ {formatMoney(formData.parcelas_customizadas.reduce((s, p) => s + p.valor, 0))}
                          </span>
                        </div>
                        {Math.abs(formData.parcelas_customizadas.reduce((s, p) => s + p.valor, 0) - (saldoRestante - (parseFloat(formData.valor_entrada) || 0))) > 0.01 && (
                          <div className="text-xs text-red-500 text-center">
                            Diferença de R$ {formatMoney(Math.abs(formData.parcelas_customizadas.reduce((s, p) => s + p.valor, 0) - (saldoRestante - (parseFloat(formData.valor_entrada) || 0))))} do valor a parcelar
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Botões */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
            <Button type="submit" disabled={saving}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Salvando...</> : editingOrder ? 'Atualizar Pedido' : 'Criar Pedido'}
            </Button>
          </div>
        </form>
        
        {/* Modal de Cadastro Completo de Produto */}
        <ProductModal
          open={showProductModal}
          onOpenChange={setShowProductModal}
          onProductCreated={(newProduct) => {
            // Adicionar produto à lista local
            setLocalProducts(prev => [...prev, newProduct]);
            // Selecionar o produto recém criado
            setProdutoAtual({
              produto_id: newProduct.id,
              nome: newProduct.nome,
              variacao_id: '',
              quantidade: 1,
              preco_unitario: newProduct.preco_base || 0,
              custo_unitario: newProduct.custo || 0,
              observacoes: '',
              volume_m3: newProduct.volume_m3,
              tipo_madeira_id: newProduct.tipo_madeira_id
            });
            setProdutoSearch(newProduct.nome);
            setProdutoMode('catalogo');
            toast.success(`Produto "${newProduct.nome}" criado! Ajuste a quantidade e adicione ao pedido.`);
          }}
          tiposMadeira={tiposMadeira}
        />
      </DialogContent>
    </Dialog>
  );
});

export default NewOrderModal;
